'use client';

import { useState, useCallback, useEffect } from 'react';
import { RefreshCw, CircleCheck, CircleAlert, CircleX, Circle, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type ServiceStatus = 'ok' | 'degraded' | 'down' | 'unknown';

interface ServiceResult {
  id: string;
  name: string;
  category: 'search' | 'legal-db' | 'ai' | 'index' | 'voice';
  status: ServiceStatus;
  latencyMs: number | null;
  detail?: string;
  error?: string;
}

interface ServicesStatusResponse {
  cached: boolean;
  timestamp: number;
  services: ServiceResult[];
}

const CATEGORY_LABELS: Record<ServiceResult['category'], string> = {
  search: 'Iskalnik',
  'legal-db': 'Pravne baze',
  ai: 'AI & Discovery',
  index: 'OpenSearch indeksi',
  voice: 'Glasovne storitve',
};

const CATEGORY_ORDER: ServiceResult['category'][] = ['search', 'index', 'legal-db', 'voice', 'ai'];

function StatusIcon({ status }: { status: ServiceStatus }) {
  switch (status) {
    case 'ok':
      return <CircleCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />;
    case 'degraded':
      return <CircleAlert className="h-4 w-4 text-amber-600 dark:text-amber-500" />;
    case 'down':
      return <CircleX className="h-4 w-4 text-red-600 dark:text-red-500" />;
    case 'unknown':
      return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return <Circle className="h-4 w-4 text-muted-foreground" />;
  }
}

function formatLatency(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('sl-SI', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function ServicesStatus() {
  const [data, setData] = useState<ServicesStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`/api/services-status${force ? '?force=1' : ''}`, {
        signal: AbortSignal.timeout(15000),
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const json = (await resp.json()) as ServicesStatusResponse;
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Napaka pri pridobivanju stanja');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus(false);
  }, [fetchStatus]);

  const grouped = data?.services.reduce<Record<string, ServiceResult[]>>((acc, s) => {
    if (!acc[s.category]) acc[s.category] = [];
    acc[s.category].push(s);
    return acc;
  }, {}) ?? {};

  const counts = data?.services.reduce(
    (acc, s) => {
      acc[s.status] = (acc[s.status] ?? 0) + 1;
      return acc;
    },
    { ok: 0, degraded: 0, down: 0, unknown: 0 } as Record<ServiceStatus, number>,
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Stanje storitev</Label>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1.5"
          disabled={loading}
          onClick={() => fetchStatus(true)}
        >
          <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
          {loading ? 'Preverjam…' : 'Preveri znova'}
        </Button>
      </div>

      {counts && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CircleCheck className="h-3 w-3 text-emerald-600" />
            {counts.ok} delujočih
          </span>
          {counts.degraded > 0 && (
            <span className="flex items-center gap-1">
              <CircleAlert className="h-3 w-3 text-amber-600" />
              {counts.degraded} omejenih
            </span>
          )}
          {counts.down > 0 && (
            <span className="flex items-center gap-1">
              <CircleX className="h-3 w-3 text-red-600" />
              {counts.down} nedelujočih
            </span>
          )}
          {data?.timestamp && (
            <span className="ml-auto">
              Zadnje preverjanje: {formatTimestamp(data.timestamp)}
              {data.cached && ' (cache)'}
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="text-xs text-red-600 dark:text-red-500 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-2">
          {error}
        </div>
      )}

      {!data && loading && (
        <div className="text-xs text-muted-foreground py-4 text-center">
          Preverjam storitve…
        </div>
      )}

      {data && (
        <div className="space-y-3">
          {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length).map((cat) => (
            <div key={cat} className="space-y-1">
              <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">
                {CATEGORY_LABELS[cat]}
              </div>
              <div className="rounded-md border border-border/50 bg-muted/20 divide-y divide-border/30">
                {grouped[cat].map((svc) => (
                  <div key={svc.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
                    <StatusIcon status={svc.status} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{svc.name}</div>
                      {(svc.detail || svc.error) && (
                        <div className={cn(
                          'text-[10px] truncate',
                          svc.error ? 'text-red-600 dark:text-red-500' : 'text-muted-foreground',
                        )}>
                          {svc.error || svc.detail}
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">
                      {formatLatency(svc.latencyMs)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        Rezultat se shrani v predpomnilnik za 60 s. Klikni &quot;Preveri znova&quot; za sveže stanje.
      </p>
    </div>
  );
}
